import { useEffect } from 'react';
import { CreditEventType, useUserNotifications } from '@monorepo/hub-common';
import type { NotificationResponse } from '@monorepo/notification-common';
import { NotificationName } from '@monorepo/notification-common';
import { shouldAutoOpenCreditEventForNotification } from './credit-events-local-storage';

function pickNewest(
  notifications: NotificationResponse[],
): NotificationResponse | undefined {
  if (!notifications.length) {
    return undefined;
  }
  return notifications.reduce((a, b) =>
    new Date(a.createdAt).getTime() >= new Date(b.createdAt).getTime() ? a : b,
  );
}

function pickExpiring(
  notifications: NotificationResponse[],
): NotificationResponse | undefined {
  const twoDay = notifications.filter(
    (n) => n.type === NotificationName.CreditExpirationWithin2Days,
  );
  const sevenDay = notifications.filter(
    (n) => n.type === NotificationName.CreditExpirationWithin7Days,
  );
  if (twoDay.length) {
    return pickNewest(twoDay);
  }
  if (sevenDay.length) {
    return pickNewest(sevenDay);
  }
  return undefined;
}

export function useCreditEventsFromNotifications(
  openCreditEventsModal: (
    type: CreditEventType,
    creditAmount: number,
    notificationId?: string,
  ) => void,
): void {
  const { data: notifications, isSuccess } = useUserNotifications();

  useEffect(() => {
    if (!isSuccess || !notifications?.length) {
      return;
    }

    const eligibleCreditNotifications = notifications.filter(
      (n) =>
        shouldAutoOpenCreditEventForNotification(n.id) &&
        (n.type === NotificationName.HubCreditReceived ||
          n.type === NotificationName.CreditExpirationWithin2Days ||
          n.type === NotificationName.CreditExpirationWithin7Days),
    );

    if (!eligibleCreditNotifications.length) {
      return;
    }

    const received = eligibleCreditNotifications.filter(
      (n) => n.type === NotificationName.HubCreditReceived,
    );
    const expiring = eligibleCreditNotifications.filter(
      (n) =>
        n.type === NotificationName.CreditExpirationWithin2Days ||
        n.type === NotificationName.CreditExpirationWithin7Days,
    );

    const receivedPick = pickNewest(received);
    const expiringPick = pickExpiring(expiring);

    if (receivedPick) {
      const amount = Number(receivedPick.values?.creditAmount);
      openCreditEventsModal(
        CreditEventType.ReceivedCredit,
        Number.isFinite(amount) ? amount : 0,
        receivedPick.id,
      );
    }

    if (expiringPick) {
      const amount = Number(expiringPick.values?.creditsCount);
      openCreditEventsModal(
        CreditEventType.ExpiringCredit,
        Number.isFinite(amount) ? amount : 0,
        expiringPick.id,
      );
    }
  }, [isSuccess, notifications, openCreditEventsModal]);
}
